import { useCallback, useSyncExternalStore } from 'react'

/**
 * Live viewport media-query state. SSR-safe (defaults to false when
 * matchMedia is unavailable). Used only for layout *structure* decisions;
 * purely visual responsive tweaks belong in index.css media queries.
 *
 * A MediaQueryList is an external store, so it is read through
 * useSyncExternalStore rather than mirrored into state: subscribing by hand
 * means an effect has to write the current match back on mount and on every
 * query change, which is a synchronous setState inside an effect and an extra
 * render each time.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {}
      }
      const mql = window.matchMedia(query)
      mql.addEventListener('change', onStoreChange)
      return () => mql.removeEventListener('change', onStoreChange)
    },
    [query]
  )

  const getSnapshot = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia(query).matches
  }, [query])

  // Rendered on the server (and in any environment without matchMedia) as the
  // desktop layout, which is what every caller here treats as its default.
  const getServerSnapshot = useCallback(() => false, [])

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/** True below the structural tablet/desktop threshold (Astryx AppShell md). */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767.98px)')
}

/**
 * True on a device whose primary input is a finger — phones and tablets — and false on a
 * desktop or laptop, however narrow its window happens to be.
 *
 * Deliberately NOT a width test. Width tells you how much room the layout has, which is a
 * different question: an iPad in landscape is 1024–1366px wide, comfortably past every
 * width breakpoint this app has, and a desktop window dragged narrow is not a tablet. The
 * pair `(hover: none) and (pointer: coarse)` is the standard reading of "the primary
 * pointer is a fingertip that cannot hover", which is exactly the phone/tablet class and
 * excludes touchscreen laptops (a mouse is still their primary pointer, so they report
 * `hover: hover` / `pointer: fine`).
 *
 * The guided editor tour is the caller: it is a driver.js spotlight walk whose stops point
 * at hover-era chrome, so it is suppressed outright on this class of device rather than
 * shown half-dead. See App.tsx.
 */
export function useIsHandheld(): boolean {
  return useMediaQuery('(hover: none) and (pointer: coarse)')
}
